import { useTranslation } from 'react-i18next';

import { TopbarWrapper } from '@/features/topbar/topbar';
import { useCreateActions } from '@/hooks/use-create-actions';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { MinimizedHeader } from '@/ui/typography/across-platform';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { AskXButton } from '../../ask-x/ask-x-button';
import { ActivateFiltersButton } from '@/components/filters/activate-filters-button';
import { activeFilterTypeState } from '@/components/filters/filter.state';
import { FilterSearchInput } from '@/components/filters/filter-search-input';
import { useHandleFilters } from '@/components/filters/use-handle-filters';
import type { CompanyFilters } from '@/features/companies/companies.state';
import {
  appliedCompaniesFiltersState,
  selectedCompaniesFiltersState,
} from '@/features/companies/companies.state';
import { useCompaniesFiltersSearchLocationSearch } from '@/features/companies/filters/use-companies-filters-location-search';
import { useCompanies } from '@/features/companies/hooks/use-companies.query';
import { colors } from '@/theme/colors';
import { useAtom, useAtomValue } from 'jotai';
import { NotificationsButton } from '@/features/notifications/notifications-button';

export const CompaniesHeader = () => {
  const { t } = useTranslation('companies');
  const { onAddOrganization } = useCreateActions();
  const { hasRequiredPermission } = useUserPermissions();

  const [activeFilterType, setActiveFilterType] = useAtom(
    activeFilterTypeState,
  );
  const appliedFilters = useAtomValue(appliedCompaniesFiltersState);
  const { updateLocationSearch, resetLocationSearch } =
    useCompaniesFiltersSearchLocationSearch();

  const { data } = useCompanies();

  const companies = data ? data.pages.flat() : [];

  const {
    handleSearchInputChange,
    hasAnyActiveFilters,
    clearSearchInput,
    switchToSearchFilter,
  } = useHandleFilters<CompanyFilters>({
    appliedFiltersAtom: appliedCompaniesFiltersState,
    selectedFiltersAtom: selectedCompaniesFiltersState,
    updateLocationSearch,
    resetLocationSearch,
  });

  const canAddCompany = hasRequiredPermission(
    PERMISSION.ADD_COMPANIES_TO_PROJECTS,
  );

  const filtersDisabled =
    (!data || companies.length === 0) && !hasAnyActiveFilters;

  return (
    <TopbarWrapper
      name="companies-header"
      justify="space-between"
      alignItems="center"
      gap="16px"
      bgColor={colors.basics.white}
      shadow={true}
    >
      <MinimizedHeader>{t`companies.title`}</MinimizedHeader>

      <Flexbox name="companies-header-actions" gap="28px" alignItems="center">
        <Flexbox
          name="filter-buttons-container"
          justify="flex-end"
          gap="10px"
          alignItems="center"
        >
          <FilterSearchInput
            onChange={handleSearchInputChange}
            placeholder={t`companies.filters.searchByName`}
            onRemove={clearSearchInput}
            disabled={filtersDisabled}
            isInputActivated={
              activeFilterType === 'search' || !!appliedFilters.search
            }
            onInputActivate={switchToSearchFilter}
            onInputDeactivate={() => setActiveFilterType(null)}
            defaultValue={appliedFilters.search ?? ''}
            displayIconOnly
          />
          <ActivateFiltersButton
            data-testid="filter-button"
            variant="text"
            isActive={activeFilterType === 'advanced'}
            disabled={filtersDisabled}
            startIcon="FilterMD"
            onMouseDown={() =>
              setActiveFilterType(current =>
                current === 'advanced' ? null : 'advanced',
              )
            }
          />
        </Flexbox>

        <NotificationsButton />

        <AskXButton />

        {canAddCompany && (
          <Button
            variant="secondary"
            startIcon="Plus"
            onClick={onAddOrganization}
          >{t`companies.addCompanyButton`}</Button>
        )}
      </Flexbox>
    </TopbarWrapper>
  );
};
